import { timeframeUnit } from '@/features/workflow-builder';
import { ConversionValueType, DEFAULT_CONVERSION_ACTION, type Conversion, type ConversionSource } from './adapter';

/**
 * What one conversion reads as: when it is counted, what it is worth, and
 * which slice of the list it falls in.
 *
 * Every function here is pure, so the answers can be read without a workspace
 * or a render.
 */

/** How many conversions one page holds. The same page as the tabs beside it. */
export const CONVERSIONS_PAGE_SIZE = 50;

/** The window in which a conversion counts, once every source has been asked. */
export type CountedWhen =
  /** Every source counts it the same way, and this is that way. */
  | { kind: 'agreed'; text: string }
  /** The sources attached to it disagree, so no one window describes it. */
  | { kind: 'varies' };

/**
 * The action as the sentence says it. A source that credits "receiving" is
 * crediting the send, which is not something the reader does.
 */
function actionText(action: string): string {
  return action === DEFAULT_CONVERSION_ACTION ? 'being sent' : action;
}

/**
 * When a conversion counts, for one window and one credited action.
 *
 * A zero window is not a missing one: it means the conversion counts however
 * long after the action it happens.
 */
export function conversionWindowText(window: number, action: string): string {
  const credited = actionText(action);
  return window > 0 ? `within ${timeframeUnit(window)} of ${credited}` : `any time after ${credited}`;
}

/** Whether two sources count the conversion the same way. */
function countsAlike(a: ConversionSource, b: ConversionSource): boolean {
  return a.window === b.window && a.action === b.action;
}

/**
 * When a conversion counts.
 *
 * The window belongs to the source, not to the conversion, so a conversion two
 * campaigns credit over different windows has no single answer and says so.
 * A conversion with no sources falls back to the goal's own window, which is
 * what the goal page shows for it.
 */
export function countedWhen(conversion: Conversion): CountedWhen {
  const [first, ...rest] = conversion.sources;

  if (first == null) {
    return { kind: 'agreed', text: conversionWindowText(conversion.window, DEFAULT_CONVERSION_ACTION) };
  }

  if (!rest.every((source) => countsAlike(first, source))) {
    return { kind: 'varies' };
  }

  return { kind: 'agreed', text: conversionWindowText(first.window, first.action) };
}

/** What a conversion is worth, as the Impact column shows it. */
export type ConversionImpact =
  /** A fixed amount, formatted in its own currency. */
  | { kind: 'value'; text: string }
  /** An amount read off each event, which has no one figure. */
  | { kind: 'attribute'; attribute: string | null }
  /** Nothing is set, so there is a value to add. */
  | { kind: 'none' };

/** The currency a goal's value is in when it names none, or names one Intl cannot read. */
const DEFAULT_CURRENCY = 'USD';

/**
 * A currency code Intl will accept: three letters, which is ISO 4217's shape.
 *
 * `Intl.NumberFormat` throws a `RangeError` on anything else — not on an
 * unknown code, which it renders as-is, but on a malformed one. The code comes
 * off the wire, so a malformed one would take out the cell mid-render.
 */
const CURRENCY_CODE = /^[A-Za-z]{3}$/;

function currencyOrDefault(currency: string | null): string {
  return currency != null && CURRENCY_CODE.test(currency) ? currency : DEFAULT_CURRENCY;
}

/** Above this, a figure is shown compactly: $3.0K rather than $3,000. */
const COMPACT_FROM = 1000;

/** The app is English, and an unpinned locale formats differently in CI. */
const LOCALE = 'en-US';

/**
 * A goal's value, in its own currency.
 *
 * Compact above a thousand and whole-numbered below it: the column is narrow,
 * and the cents of a conversion's value are not what the row is being read
 * for. The currency decides its own decimals, so a yen figure keeps none.
 *
 * The locale is pinned rather than read off the reader, as it is everywhere
 * else this app formats money: an unpinned locale renders dollars as `US$`
 * wherever the runtime is not American, including CI.
 */
export function formatConversionValue(value: number, currency: string | null): string {
  const isCompact = Math.abs(value) >= COMPACT_FROM;

  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: currencyOrDefault(currency),
    ...(isCompact
      ? { notation: 'compact' as const, minimumSignificantDigits: 2, maximumSignificantDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  }).format(value);
}

/**
 * What a conversion is worth, from the goal's own value fields.
 *
 * The declared value type decides, not whichever fields happen to be filled
 * in: a goal switched back to no value keeps the amount it used to be worth,
 * and reading that amount would put a figure in the column for a conversion
 * the workspace has said is worth nothing.
 */
export function conversionImpact(conversion: Conversion): ConversionImpact {
  if (conversion.valueType === ConversionValueType.EventAttribute) {
    return { kind: 'attribute', attribute: conversion.valueAttribute };
  }

  if (conversion.valueType === ConversionValueType.None || conversion.value == null) {
    return { kind: 'none' };
  }

  return { kind: 'value', text: formatConversionValue(conversion.value, conversion.currency) };
}

/** How many things a conversion is used by, across every kind of source. */
export function usageTotal(conversion: Conversion): number {
  const { campaigns, newsletters, transactional } = conversion.usage;
  return campaigns + newsletters + transactional;
}

/**
 * The conversions a search names.
 *
 * The whole list arrives in one read, so the search runs here rather than as
 * a request. It matches the name alone: that is what the reader typed and what
 * the column they are looking at shows.
 */
export function searchConversions(conversions: readonly Conversion[], search: string): readonly Conversion[] {
  const term = search.trim().toLowerCase();
  if (term === '') {
    return conversions;
  }

  return conversions.filter((conversion) => conversion.name.toLowerCase().includes(term));
}

/**
 * One page of conversions.
 *
 * Pages are 1-based, and a page past the end is empty rather than clamped: a
 * search that narrows the list under the page someone is on should show them
 * nothing on it, not silently move them.
 */
export function pageOfConversions(
  conversions: readonly Conversion[],
  page: number,
  pageSize: number = CONVERSIONS_PAGE_SIZE,
): Conversion[] {
  const start = Math.max(0, (page - 1) * pageSize);
  return conversions.slice(start, start + pageSize);
}

/** The list a reader is looking at: searched first, then cut into pages. */
export type ConversionsView = {
  /** The rows on the current page. */
  rows: Conversion[];
  /** How many rows the search left, which is what the pager counts. */
  total: number;
};

export function conversionsView(
  conversions: readonly Conversion[],
  search: string,
  page: number,
  pageSize: number = CONVERSIONS_PAGE_SIZE,
): ConversionsView {
  const matched = searchConversions(conversions, search);
  return { rows: pageOfConversions(matched, page, pageSize), total: matched.length };
}
